/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BR-16_INVENTORY_STATUS_BOX_CONFIRM
 * Date:         2026-02-08
 * Modified By:  Cursor
 *
 * Description:
 * Replaced localStorage-based "last check" with real inventoryConfirmedAt from user profile.
 * Confirm button now calls POST /api/inventory/confirm via service function.
 * Added confirmation popup modal matching Figma design.
 * Added urgency states: normal (green), warning (orange, 8+ days), critical (red, 25+ days).
 *
 * File Size Check:
 * - Current: 183
 * - Adding: ~0 (rewrite)
 * - Projected: ~155
 * - Decision: Proceed
 *
 * Typography Compliance:
 * - Utilities used: .u-H2-24, .u-P2-16, .u-P3-14, .u-P2-16-medium
 * - Tokens used: via SCSS variables only
 *
 * Related Files:
 * - client/src/features/dashboard/InventoryStatusBox.tsx
 * - client/src/features/dashboard/InventoryConfirmModal.tsx (NEW)
 * - client/src/styles/components/dashboard/_profile-boxes.scss
 * - client/src/services/inventoryService.ts
 * - client/src/services/usersService.ts
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getInventoryItems, confirmInventory } from "../../services/inventoryService";
import { getUserProfile } from "../../services/usersService";
import { useIsGuest } from "../../contexts/GuestModeContext"; // [2026-07-06] GUEST_ACCESS BR-09/10
import InventoryConfirmModal from "./InventoryConfirmModal";

// [2026-02-08] BR-16: Inventory status box with real confirmation tracking
const InventoryStatusBox: React.FC = () => {
  const navigate = useNavigate();
  const isGuest = useIsGuest(); // [2026-07-06] GUEST_ACCESS: show box as N/A + locked button
  const [inventoryCount, setInventoryCount] = useState(0);
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // [2026-02-08] BR-16: Fetch inventory count AND user profile for confirmedAt
  useEffect(() => {
    // [2026-07-06] GUEST_ACCESS: no inventory/profile for guests — skip the authed fetch.
    if (isGuest) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const [invRes, profileRes] = await Promise.all([
          getInventoryItems({ limit: 1, page: 1 }),
          getUserProfile(),
        ]);
        setInventoryCount(invRes.data.total || 0);
        setConfirmedAt(profileRes.data?.inventoryConfirmedAt || null);
      } catch (error) {
        console.error("Failed to fetch inventory status:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isGuest]);

  const hasInventory = inventoryCount > 0;

  // [2026-02-08] BR-16: Calculate days since last confirmation
  const daysSinceConfirm = confirmedAt
    ? Math.floor((Date.now() - new Date(confirmedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // [2026-02-08] BR-16: Determine status display based on inventory state
  const getStatusDisplay = () => {
    if (!hasInventory) {
      return {
        text: "Add your first inventory item",
        color: "var(--color-status-orange)",
        lastUpdateColor: undefined,
        borderColor: undefined,
      };
    }

    if (daysSinceConfirm === null) {
      // Has inventory but never confirmed (shouldn't happen after upload, but handle it)
      return {
        text: "Please confirm your inventory",
        color: "var(--color-status-orange)",
        lastUpdateColor: "var(--color-status-orange)",
        borderColor: undefined,
      };
    }

    if (daysSinceConfirm >= 25) {
      // Critical: removal imminent
      const daysLeft = Math.max(1, 30 - daysSinceConfirm);
      return {
        text: `Inventory will be removed in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`,
        color: "var(--color-status-red)",
        lastUpdateColor: "var(--color-status-red)",
        borderColor: "var(--color-status-red)",
      };
    }

    if (daysSinceConfirm >= 8) {
      // Warning: overdue
      return {
        text: "Your inventory needs confirmation",
        color: "var(--color-status-orange)",
        lastUpdateColor: "var(--color-status-orange)",
        borderColor: "var(--color-status-orange)",
      };
    }

    // Normal: recently confirmed
    return {
      text: "Is your inventory up to date?",
      color: "var(--color-status-green)",
      lastUpdateColor: undefined,
      borderColor: undefined,
    };
  };

  const status = getStatusDisplay();

  // [2026-02-08] BR-16: Open confirmation modal
  const handleConfirmClick = () => {
    setShowConfirmModal(true);
  };

  // [2026-02-08] BR-16: Submit confirmation via API
  const handleConfirmSubmit = async () => {
    setIsConfirming(true);
    try {
      const res = await confirmInventory();
      setConfirmedAt(res.data.confirmedAt);
      setShowConfirmModal(false);
    } catch (err) {
      console.error("Failed to confirm inventory:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  // Navigate to inventory page
  const handleEditInventory = () => {
    navigate("/inventory");
  };

  if (loading) {
    return (
      <div className="profile-box">
        <div className="profile-box-header">
          <div className="profile-box-icon" />
          <h4 className="profile-box-title u-H2-24">Inventory Status</h4>
        </div>
        <p className="u-P2-16 profile-box-loading-text">Loading...</p>
      </div>
    );
  }

  return (
    <div
      className="profile-box"
      style={status.borderColor ? { borderColor: status.borderColor } : undefined}
    >
      <div className="profile-box-header">
        <h4
          className="profile-box-title u-H2-24"
          title="Keeping your inventory accurate helps avoid unnecessary inquiries"
        >
          Inventory Status
        </h4>
      </div>

      {/* [2026-02-08] BR-16: Show last confirmed date from real data */}
      {hasInventory && daysSinceConfirm !== null && (
        <p
          className="u-P3-14 inventory-last-update"
          style={status.lastUpdateColor ? { color: status.lastUpdateColor } : undefined}
        >
          Last update: {daysSinceConfirm === 0 ? "today" : `${daysSinceConfirm} day${daysSinceConfirm !== 1 ? "s" : ""} ago`}
        </p>
      )}

      <div className="profile-box-status">
        {/* [2026-07-06] GUEST_ACCESS: guests see N/A instead of a real inventory status. */}
        <p
          className="u-P2-16 profile-box-status-text"
          style={{ color: isGuest ? "var(--color-grey-1)" : status.color }}
        >
          {isGuest ? "N/A" : status.text}
        </p>
      </div>

      <div className="profile-box-actions inventory-actions">
        {/* [2026-07-06] GUEST_ACCESS: guests see Edit Inventory but it is locked. */}
        <button
          type="button"
          className="u-P2-16-medium btn-inventory-action"
          onClick={handleEditInventory}
          disabled={isGuest}
          aria-disabled={isGuest}
          title={isGuest ? "Sign in to manage inventory" : undefined}
          style={isGuest ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          aria-label="Edit inventory"
        >
          Edit Inventory
        </button>
        {!isGuest && hasInventory && (
          <button
            type="button"
            className="u-P2-16-medium btn-inventory-action"
            onClick={handleConfirmClick}
            aria-label="Confirm inventory is up to date"
          >
            Confirm
          </button>
        )}
      </div>

      {/* [2026-02-08] BR-16: Confirmation popup */}
      <InventoryConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubmit}
        onEditInventory={() => { setShowConfirmModal(false); navigate("/inventory"); }}
        isConfirming={isConfirming}
      />
    </div>
  );
};

export default InventoryStatusBox;
